import type { ProductConfig } from '@/types/product';
import type { ScoringCategory } from '@/types';
import { QUESTIONS } from '@/services/questionnaire/question-bank';

const questionCategories = {
  ...Object.fromEntries(QUESTIONS.map(q => [q.id, q.category as ScoringCategory])),
  fin_payback: 'indicadores_financeiros' as ScoringCategory
} as Record<string, ScoringCategory>;

export const franqueabilidadeConfig: ProductConfig = {
  id: 'franqueabilidade',
  title: 'Diagnóstico de Franqueabilidade',
  description: 'Este diagnóstico é uma ferramenta estratégica fundamental para avaliar o nível de franqueabilidade de um negócio. A análise combina inteligência de dados (Business Intelligence), benchmarking do mercado de franquias e a experiência prática da Inova Franquias com marcas em operação no setor, incluindo referências e tendências observadas no ecossistema da ABF. O objetivo é oferecer uma visão clara sobre o potencial de expansão da marca e indicar caminhos para estruturar um modelo de franquia sólido e escalável.',
  funilId: 63,
  crmOrigem: 'Diagnóstico Franqueabilidade',
  questions: QUESTIONS,
  pdf: 'franqueabilidade',
  scoring: {
    categories: [
      'credibilidade_marca',
      'modelo_negocio_padronizacao',
      'perfil_franqueador',
      'diferenciais_competitivos',
      'capacidade_expansao',
      'registro_marca',
      'indicadores_financeiros'
    ],
    weights: {
      credibilidade_marca: 40,
      modelo_negocio_padronizacao: 10,
      perfil_franqueador: 20,
      diferenciais_competitivos: 10,
      capacidade_expansao: 20,
      registro_marca: 10,
      indicadores_financeiros: 20  // fin_gross_revenue (10) + fin_payback (10)
    },
    labels: {
      credibilidade_marca: 'Credibilidade da Marca',
      modelo_negocio_padronizacao: 'Modelo de Negócio e Padronização',
      perfil_franqueador: 'Perfil do Franqueador',
      diferenciais_competitivos: 'Diferenciais Competitivos',
      capacidade_expansao: 'Capacidade de Expansão',
      registro_marca: 'Registro de Marca',
      indicadores_financeiros: 'Indicadores Financeiros'
    },
    maxTotal: 130,
    improvements: {
      credibilidade_marca: 1.0,
      modelo_negocio_padronizacao: 1.0,
      perfil_franqueador: 1.0,
      diferenciais_competitivos: 1.0,
      capacidade_expansao: 1.0,
      registro_marca: 1.0,
      indicadores_financeiros: 1.0
    },
    maxDifficulty: {
      credibilidade_marca: 'easy'
    },
    nonScoringCategories: [],
    scoreThresholds: [70, 50],
    questionCategories,
    finalRecommendations: ['Alta Franqueabilidade', 'Franqueabilidade em Desenvolvimento', 'Perfil Não Recomendado'],
    recommendationMaps: {
      specific: {
        cred_website: {
          nao: 'Desenvolver um site profissional é fundamental para credibilidade. Invista em um site responsivo com informações claras sobre o negócio.',
          sim: 'Seu site profissional agrega credibilidade à marca e facilita a captação de franqueados.'
        },
        cred_instagram: {
          nao: 'Ter presença ativa no Instagram é essencial para visibilidade da marca. Recomendamos criar um perfil comercial e manter postagens regulares.',
          sim: 'Perfil no Instagram ativo contribui para o fortalecimento da marca.'
        },
        cred_years: {
          menos_1: 'Seu negócio ainda está em fase inicial de consolidação. Fortalecer o tempo de mercado antes de franquear é fundamental.',
          '1_a_2': 'Com 1 a 2 anos de mercado, sua empresa está em fase de amadurecimento. Continue construindo histórico antes de franquear.',
          '2_a_5': 'Bom tempo de mercado! Sua empresa já tem experiência suficiente para iniciar o processo de franqueamento.',
          mais_5: 'Excelente! Mais de 5 anos de mercado demonstram solidez e maturidade para franquear com segurança.'
        },
        reg_inpi: {
          nao: 'Como empresário, um dos principais ativos é a sua marca. É recomendado registrá-la junto ao INPI para proteção legal antes de franquear.',
          sim: 'Marca registrada no INPI garante proteção legal e segurança jurídica para toda a rede de franquias.'
        }
      },
      general: {
        credibilidade_marca: {
          low: 'A credibilidade da marca precisa ser fortalecida. Invista em presença digital, tempo de mercado e expansão de unidades.',
          medium: 'Sua credibilidade de marca está em desenvolvimento. Continue fortalecendo presença online e expandindo unidades.',
          high: 'Excelente credibilidade de marca! Sua empresa já possui boa reputação e presença no mercado.'
        },
        modelo_negocio_padronizacao: {
          low: 'A padronização do modelo de negócio é essencial para franquear. A Inova pode ajudar a documentar e estruturar seus processos.',
          medium: 'Seu modelo está parcialmente padronizado. Complete a documentação para garantir replicabilidade.',
          high: 'Modelo de negócio bem documentado e padronizado! Sua operação está pronta para ser replicada.'
        },
        perfil_franqueador: {
          low: 'É importante alinhar suas expectativas e recursos antes de iniciar o processo de franqueamento.',
          medium: 'Seu perfil como franqueador está se desenvolvendo. Continue planejando sua estratégia de expansão.',
          high: 'Perfil de franqueador bem definido! Você está comprometido e preparado para expandir via franquias.'
        },
        diferenciais_competitivos: {
          low: 'Diferenciais competitivos pouco claros. É fundamental identificar e fortalecer o que torna seu negócio único.',
          medium: 'Alguns diferenciais identificados. Continue desenvolvendo seus pontos fortes para se destacar.',
          high: 'Fortes diferenciais competitivos! Sua marca se destaca claramente no mercado.'
        },
        capacidade_expansao: {
          low: 'Capacidade de expansão limitada. É necessário desenvolver estrutura logística e superar desafios operacionais.',
          medium: 'Capacidade de expansão moderada. Alguns ajustes podem ampliar seu potencial de crescimento.',
          high: 'Grande capacidade de expansão! Seu modelo tem condições de crescer em todo o Brasil.'
        },
        registro_marca: {
          low: 'Registrar sua marca no INPI é um passo essencial antes de franquear. Proteja seu ativo mais importante.',
          medium: 'Registrar sua marca no INPI é um passo essencial antes de franquear. Proteja seu ativo mais importante.',
          high: 'Marca registrada! Sua empresa está protegida legalmente para expandir via franquias com segurança.'
        },
        indicadores_financeiros: {
          low: 'Faturamento abaixo da média de mercado. Aumentar o volume de vendas é fundamental para sustentar a rede de franquias.',
          medium: 'Faturamento próximo à média. Otimizar processos e aumentar o ticket médio pode fortalecer os indicadores financeiros.',
          high: 'Ótimos indicadores financeiros! Seu faturamento demonstra solidez para suportar a expansão via franquias.'
        }
      }
    }
  }
};
